/**
 * 固定容量对象池：热循环零分配。
 * 池内对象以 `_active` 布尔标记是否在用。
 * 纯 ESM，不依赖浏览器 API，node 可直接 import。
 */
export class Pool {
  /**
   * @param {number} capacity 最大同时活跃对象数
   * @param {() => object} factory 创建新对象的工厂函数
   */
  constructor(capacity, factory) {
    this.capacity = capacity;
    this.factory = factory;
    /** @type {object[]} 全量对象（含空闲） */
    this.items = [];
    this._activeCount = 0;
  }

  /**
   * 取一个空闲对象；池满返回 null。
   * 首次调用时按需用 factory 创建，之后复用。
   * @returns {object|null}
   */
  obtain() {
    for (let i = 0; i < this.items.length; i++) {
      const obj = this.items[i];
      if (!obj._active) {
        obj._active = true;
        this._activeCount++;
        return obj;
      }
    }
    if (this.items.length < this.capacity) {
      const obj = this.factory();
      obj._active = true;
      this.items.push(obj);
      this._activeCount++;
      return obj;
    }
    return null;
  }

  /**
   * 归还对象（幂等，重复 release 安全）。
   * @param {object} obj
   */
  release(obj) {
    if (obj._active) {
      obj._active = false;
      this._activeCount--;
    }
  }

  /**
   * 倒序遍历活跃对象，允许在回调中 release 当前对象。
   * @param {(obj: object, index: number) => void} fn
   */
  forEachActive(fn) {
    for (let i = this.items.length - 1; i >= 0; i--) {
      const obj = this.items[i];
      if (obj._active) fn(obj, i);
    }
  }

  /** 清空池：所有对象标记为空闲。 */
  clear() {
    for (let i = 0; i < this.items.length; i++) {
      this.items[i]._active = false;
    }
    this._activeCount = 0;
  }

  /** 当前活跃对象数 */
  get activeCount() {
    return this._activeCount;
  }
}
